'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { FOCUS_STYLES } from '@ui/lib/focus-styles';
import { cn } from '@ui/lib/utils';
import { X } from 'lucide-react';
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { DialogDescription, DialogTitle } from './dialog';
import { VisuallyHidden } from './visually-hidden';

interface DialogOptions {
  content: ReactNode;
  preventOutsideClose?: boolean;
  className?: string;
}

interface GlobalDialogContextType {
  openDialog: (options: DialogOptions) => void;
  closeDialog: () => void;
}

const GlobalDialogContext = createContext<GlobalDialogContextType | undefined>(
  undefined
);

// Custom high z-index overlay for global dialogs
const GlobalDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[79] bg-black/40 backdrop-blur-md hover:backdrop-blur-none transition-all duration-300 ease-in data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 group',
      className
    )}
    {...props}
  >
    <div className="absolute inset-0 flex items-center justify-center">
      <button
        className={cn(
          'flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2.5 shadow-lg ring-1 ring-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in hover:ring-white/40 hover:shadow-xl',
          FOCUS_STYLES.overlay
        )}
      >
        <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white text-black ring-1 ring-white/40">
          <X className="h-3.5 w-3.5" />
        </div>
        <span className="text-sm font-bold text-white drop-shadow-sm">
          Click to Close
        </span>
      </button>
    </div>
  </DialogPrimitive.Overlay>
));
GlobalDialogOverlay.displayName = 'GlobalDialogOverlay';

// Custom high z-index content for global dialogs
const GlobalDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    onCustomClose?: () => void;
  }
>(({ className, children, onCustomClose, ...props }, ref) => {
  // Check for any DialogTitle in children
  const hasDialogTitle = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === DialogTitle
  );

  // Check for any DialogDescription in children
  const hasDialogDescription = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === DialogDescription
  );

  return (
    <DialogPrimitive.Portal>
      <GlobalDialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-[80] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg shadow-slate-900/20 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg',
          className
        )}
        {...props}
      >
        {/* Add a default title if one isn't provided */}
        {!hasDialogTitle && (
          <VisuallyHidden asChild>
            <DialogTitle className="text-base font-bold text-slate-800 text-align-left">
              Dialog
            </DialogTitle>
          </VisuallyHidden>
        )}

        {/* Add a default description if one isn't provided */}
        {!hasDialogDescription && (
          <VisuallyHidden asChild>
            <DialogDescription>Dialog content</DialogDescription>
          </VisuallyHidden>
        )}

        {children}

        {/* Custom close button that calls our global dialog close function */}
        <button
          type="button"
          onClick={onCustomClose}
          className="absolute right-4 top-4 rounded-lg opacity-70 ring-offset-background hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground inline-flex items-center gap-x-1.5 bg-white px-1.5 py-1.5 text-xs font-bold text-slate-800 shadow-sm  hover:bg-slate-50/80 transition duration-200 ease-in-out"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
GlobalDialogContent.displayName = 'GlobalDialogContent';

export function GlobalDialogProvider({ children }: { children: ReactNode }) {
  const [dialogOptions, setDialogOptions] = useState<DialogOptions | null>(
    null
  );
  const [open, setOpen] = useState(false);

  const openDialog = (options: DialogOptions) => {
    setDialogOptions(options);
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    // Clear options after animation completes
    setTimeout(() => {
      setDialogOptions(null);
    }, 300);
  };

  return (
    <GlobalDialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      {dialogOptions && (
        <DialogPrimitive.Root
          open={open}
          onOpenChange={(isOpen) => {
            if (!isOpen && !dialogOptions.preventOutsideClose) {
              closeDialog();
            }
          }}
        >
          <GlobalDialogContent
            className={dialogOptions.className}
            onCustomClose={closeDialog}
            onEscapeKeyDown={(e) => {
              if (dialogOptions.preventOutsideClose) {
                e.preventDefault();
              }
            }}
            onPointerDownOutside={(e) => {
              if (dialogOptions.preventOutsideClose) {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              if (dialogOptions.preventOutsideClose) {
                e.preventDefault();
              }
            }}
          >
            {dialogOptions.content}
          </GlobalDialogContent>
        </DialogPrimitive.Root>
      )}
    </GlobalDialogContext.Provider>
  );
}

export function useGlobalDialog() {
  const context = useContext(GlobalDialogContext);
  if (context === undefined) {
    throw new Error(
      'useGlobalDialog must be used within a GlobalDialogProvider'
    );
  }
  return context;
}
